/*
 * bc-accessories
 *
 * "Frequently bought together" widget rendered by snippets/bc-accessories.liquid
 * (placed by blocks/bc-accessories.liquid below the buy buttons). Each row is one
 * accessory product with a variant <select> (or hidden input for single-variant
 * products), a qty stepper, a live line price, a status note and an include
 * checkbox.
 *
 * Single-button model: there is NO separate add button. The checked accessories
 * "ride along" with the product's own Add-to-cart — we listen for the main
 * product add (CartAddEvent, source 'product-form-component' or 'bc-custom-length',
 * scoped to THIS product's id) and then POST the checked accessories to
 * /cart/add.js in one `items: [...]` request (the same batch-add pattern as
 * bc-custom-length.js), re-dispatching cart:update so the drawer shows everything.
 * This rides on top of Horizon's own add path rather than intercepting it, so the
 * custom-length flow, selling plans, line-item properties and sticky bar all keep
 * working untouched. Checks are cleared after a successful ride-along so a second
 * Add-to-cart doesn't silently re-add them.
 *
 * Out-of-stock vs pre-order: each row carries a per-variant map
 * (script[data-bc-acc-variants]) of { price, available, preorder, shipDate }.
 * `available` is Shopify's source of truth — true for in-stock AND pre-order
 * (continue-selling), false only for truly unpurchasable. We block the latter
 * (checkbox disabled, filtered out before POST) and allow-but-label the former.
 */
(() => {
  if (customElements.get('bc-accessories')) return;

  // Cart-add sources we treat as "the main product was just added" — i.e. a
  // ride-along trigger. Cart line edits and our own re-dispatch are excluded.
  const ADD_SOURCES = ['product-form-component', 'bc-custom-length'];

  class BcAccessories extends HTMLElement {
    connectedCallback() {
      this.locale = this.dataset.locale || 'en';
      this.currency = this.dataset.currency || 'USD';
      this.productId = this.dataset.productId || '';
      this._adding = false;

      this.formatter = window.bcMoney.formatter(this.locale, this.currency);

      this.rows = Array.from(this.querySelectorAll('[data-bc-acc-row]')).map((el) => {
        let map = {};
        try {
          const script = el.querySelector('script[data-bc-acc-variants]');
          if (script) map = JSON.parse(script.textContent || '{}');
        } catch (_e) {
          map = {};
        }
        return {
          el,
          map,
          variant: el.querySelector('[data-key="variant"]'),
          qty: el.querySelector('[data-key="qty"]'),
          minus: el.querySelector('[data-key="minus"]'),
          plus: el.querySelector('[data-key="plus"]'),
          linePrice: el.querySelector('[data-key="line-price"]'),
          note: el.querySelector('[data-key="note"]'),
          noteText: el.querySelector('[data-key="note-text"]'),
          include: el.querySelector('[data-key="include"]'),
        };
      });

      this.hintEl = this.querySelector('[data-key="hint"]');
      this.emptyHint = this.hintEl ? this.hintEl.textContent.trim() : '';
      this.errorEl = this.querySelector('[data-key="error"]');

      // `input` re-prices live without rewriting the value being typed; `change`
      // (blur / select / checkbox) normalizes the qty fields first so an
      // out-of-range or empty entry settles to a valid number.
      this._onInput = () => this._recompute();
      this._onChange = () => {
        this._normalizeQty();
        this._recompute();
      };
      this.addEventListener('input', this._onInput);
      this.addEventListener('change', this._onChange);

      this.rows.forEach((row) => {
        row.minus?.addEventListener('click', () => this._step(row, -1));
        row.plus?.addEventListener('click', () => this._step(row, +1));
      });

      // Ride-along trigger: the product's own Add-to-cart dispatches cart:update.
      this._onCartUpdate = (event) => this._maybeRideAlong(event);
      document.addEventListener('cart:update', this._onCartUpdate);

      this._recompute();
    }

    disconnectedCallback() {
      this.removeEventListener('input', this._onInput);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('cart:update', this._onCartUpdate);
    }

    _money(cents) {
      return this.formatter.format((Number(cents) || 0) / 100);
    }

    _readQty(row) {
      const min = parseInt(row.qty?.dataset.min, 10) || 1;
      const max = parseInt(row.qty?.dataset.max, 10) || 99;
      let q = parseInt(row.qty?.value, 10);
      if (!Number.isFinite(q) || q < min) q = min;
      if (q > max) q = max;
      return q;
    }

    _selectedVariant(row) {
      const id = row.variant?.value;
      if (!id) return { id: null, info: null };
      return { id, info: row.map[id] || null };
    }

    _step(row, delta) {
      if (!row.qty) return;
      const min = parseInt(row.qty.dataset.min, 10) || 1;
      const max = parseInt(row.qty.dataset.max, 10) || 99;
      const q = Math.min(max, Math.max(min, this._readQty(row) + delta));
      row.qty.value = String(q);
      row.qty.setAttribute('aria-valuenow', String(q));
      this._recompute();
    }

    _normalizeQty() {
      this.rows.forEach((row) => {
        if (!row.qty) return;
        const q = this._readQty(row);
        row.qty.value = String(q);
        row.qty.setAttribute('aria-valuenow', String(q));
      });
    }

    // Refresh every row's line price + status note, sync per-row checkbox enabled
    // state to the selected variant's availability, and roll the selected count +
    // total into the hint line. Pure read/render — no network.
    _recompute() {
      let count = 0;
      let total = 0;

      this.rows.forEach((row) => {
        const qty = this._readQty(row);
        const { info } = this._selectedVariant(row);

        if (row.linePrice && info) {
          row.linePrice.textContent = this._money(info.price * qty);
        }

        this._renderNote(row, info);

        // A variant that isn't purchasable can't be added — disable + clear the
        // checkbox so it never contributes to the ride-along.
        if (row.include) {
          const purchasable = !!(info && info.available);
          if (!purchasable) {
            row.include.checked = false;
            row.include.disabled = true;
          } else {
            row.include.disabled = false;
          }
          if (row.include.checked && purchasable) {
            count += 1;
            total += info.price * qty;
          }
        }
      });

      if (this.hintEl) {
        if (count > 0) {
          const noun = count === 1 ? 'accessory' : 'accessories';
          this.hintEl.textContent = `${count} ${noun} · +${this._money(total)} added with your order`;
          this.hintEl.classList.add('bc-accessories__hint--active');
        } else {
          this.hintEl.textContent = this.emptyHint;
          this.hintEl.classList.remove('bc-accessories__hint--active');
        }
      }
    }

    // Status note states mirror bc-stock-indicator: in_stock / low / preorder / out.
    _renderNote(row, info) {
      if (!row.note) return;
      let state = '';
      let text = '';
      switch (info && info.state) {
        case 'preorder':
          state = 'preorder';
          text = info.shipDate ? `Pre-order — ships ${info.shipDate}` : 'Pre-order';
          break;
        case 'low':
          state = 'low';
          text = info.qty > 0 ? `Low Stock - ${info.qty} left` : 'Low stock';
          break;
        case 'in_stock':
          state = 'in_stock';
          text = 'In stock';
          break;
        case 'out':
          state = 'out';
          text = 'Out of stock';
          break;
      }
      row.note.className = `bc-accessories__note${state ? ` bc-accessories__note--${state}` : ''}`;
      if (row.noteText) row.noteText.textContent = text;
      row.note.hidden = text === '';
    }

    _collectItems() {
      const items = [];
      this.rows.forEach((row) => {
        if (!row.include || !row.include.checked || row.include.disabled) return;
        const { id, info } = this._selectedVariant(row);
        if (!id || !info || !info.available) return; // final guard against sold-out
        items.push({ id: Number(id), quantity: this._readQty(row) });
      });
      return items;
    }

    // Triggered by the product's own Add-to-cart. Add the checked accessories as a
    // follow-up so they land in the same cart action.
    _maybeRideAlong(event) {
      if (this._adding) return; // ignore our own re-dispatch / re-entrancy
      const data = (event && event.detail && event.detail.data) || {};
      if (data.didError) return; // main add failed — don't pile accessories on
      if (!ADD_SOURCES.includes(data.source)) return; // not a main-product add
      // Scope to THIS product so a quick-add of some other product on the page
      // (also source 'product-form-component') doesn't drag our accessories in.
      if (this.productId && String(data.productId || '') !== String(this.productId)) return;

      const items = this._collectItems();
      if (!items.length) return;
      this._rideAlong(items);
    }

    async _rideAlong(items) {
      this._adding = true;
      this._setError('');
      this.classList.add('is-loading');

      try {
        const { ok, response, cart } = await window.bcCartAdd.add(items);

        if (!ok) {
          // Main product is already in the cart; surface the accessory failure and
          // keep the checks so the customer can adjust and the next add retries.
          // The main add opened the cart drawer over the PDP, so also route the
          // error through the theme's cart-error channel (same as a failed
          // add-to-cart) rather than relying only on the inline message.
          const msg = response.description || response.message || 'Could not add accessories.';
          this._setError(msg);
          this._emitCartError(response.message || msg, response.description, response.errors);
          return;
        }

        // Clear the selection so a later Add-to-cart doesn't re-add them.
        this.rows.forEach((row) => {
          if (row.include) row.include.checked = false;
        });

        // Re-broadcast so the cart drawer / sections morph to include the
        // accessories on top of the main item the product form just added.
        window.bcCartAdd.dispatchCartUpdate(this, {
          source: 'bc-accessories',
          cart,
          response,
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
        });
      } catch (err) {
        console.error('[bc-accessories] ride-along cart/add failed:', err);
        const msg = 'Could not add accessories. Please try again.';
        this._setError(msg);
        this._emitCartError(msg);
      } finally {
        this._adding = false;
        this.classList.remove('is-loading');
        this._recompute();
      }
    }

    _setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }

    // Route a ride-along failure through the theme's cart-error channel
    // (ThemeEvents.cartError === 'cart:error') matching CartErrorEvent's detail
    // shape, so it's handled like any other failed add (e.g. sticky bar reset)
    // instead of being trapped in the PDP panel behind the open cart drawer.
    _emitCartError(message, description, errors) {
      this.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            sourceId: 'bc-accessories',
            data: { message, description, errors },
          },
        })
      );
    }
  }

  customElements.define('bc-accessories', BcAccessories);
})();
